
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useProject } from '@/hooks/useProject';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ArrowLeft, Pencil, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { EditClientDialog } from './components/EditClientDialog';
import { DeleteClientDialog } from './components/DeleteClientDialog';
import { Card, CardContent } from '@/components/ui/card';

const ClientDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { projectClient, currentProject } = useProject();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  
  // Convert id from string to number for database queries
  const clientId = id ? parseInt(id) : 0;

  const { data: client, isLoading: isLoadingClient } = useQuery({
    queryKey: ['client', currentProject, clientId],
    queryFn: async () => {
      const { data, error } = await projectClient
        .from('clientes')
        .select('*')
        .eq('id', clientId)
        .eq('project_id', projectClient.getProjectId())
        .single();
      
      if (error) throw error;
      return data;
    },
  });

  const { data: launches, isLoading: isLoadingLaunches } = useQuery({
    queryKey: ['client-launches', currentProject, clientId],
    queryFn: async () => {
      const { data, error } = await projectClient
        .from('form_launches')
        .select(`
          *,
          jotform_form_mappings:form_mapping_id (jotform_form_id, form_title),
          report_templates_v2:template_v2_id (name),
          report_templates (name)
        `)
        .eq('id_cliente', clientId)
        .eq('project_id', projectClient.getProjectId());
      
      if (error) throw error;
      return data;
    },
  });

  if (isLoadingClient || isLoadingLaunches) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-8 w-48" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-10 w-36" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardContent className="p-6 space-y-4">
              <Skeleton className="w-32 h-32" />
              <div>
                <Skeleton className="h-6 w-20 mb-2" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4 mt-1" />
              </div>
            </CardContent>
          </Card>
          <div>
            <Skeleton className="h-7 w-32 mb-4" />
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead><Skeleton className="h-4 w-8" /></TableHead>
                    <TableHead><Skeleton className="h-4 w-24" /></TableHead>
                    <TableHead><Skeleton className="h-4 w-20" /></TableHead>
                    <TableHead><Skeleton className="h-4 w-20" /></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...Array(3)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton className="h-4 w-8" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                      <TableCell><Skeleton className="h-8 w-24" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/clients')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <h1 className="text-2xl font-bold">
            {client?.nombre}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setIsEditOpen(true)}
            variant="outline"
            aria-label="Editar cliente"
          >
            <Pencil className="w-4 h-4 mr-2" aria-hidden="true" />
            Editar Cliente
          </Button>
          <Button
            onClick={() => setIsDeleteOpen(true)}
            variant="outline"
            className="text-destructive hover:text-destructive"
            aria-label="Eliminar cliente"
          >
            <Trash2 className="w-4 h-4 mr-2" aria-hidden="true" />
            Eliminar Cliente
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardContent className="p-6 space-y-4">
            {client?.logo && (
              <img
                src={client.logo}
                alt={`Logo de ${client.nombre}`}
                className="w-32 h-32 object-contain mb-4"
              />
            )}
            
            {client?.mision && (
              <div>
                <h2 className="text-lg font-semibold mb-2">Misión</h2>
                <p className="text-muted-foreground">{client.mision}</p>
              </div>
            )}
          </CardContent>
        </Card>
        
        <div>
          <h2 className="text-xl font-semibold mb-4">Lanzamientos</h2>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Formulario</TableHead>
                <TableHead>Plantilla</TableHead>
                <TableHead>Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {launches?.map((launch) => {
                // Mostrar datos del formulario
                const formName = launch.jotform_form_mappings?.form_title ||
                                launch.jotform_form_mappings?.jotform_form_id ||
                                'N/A';
                const templateName = launch.report_templates_v2?.name ||
                                   launch.report_templates?.name ||
                                   'N/A';
                const isV2 = launch.form_mapping_id && launch.template_v2_id;
                
                return (
                  <TableRow key={launch.id}>
                    <TableCell>
                      {launch.id}
                      {isV2 && <span className="ml-2 text-xs bg-green-100 text-green-800 px-1 rounded">V2</span>}
                    </TableCell>
                    <TableCell>{formName}</TableCell>
                    <TableCell>{templateName}</TableCell>
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        onClick={() => navigate(`/admin/launches/${launch.id}`)}
                      >
                        Ver detalles
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {(!launches || launches.length === 0) && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-4 text-muted-foreground">
                    No hay lanzamientos para este cliente
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {client && (
        <>
          <EditClientDialog
            client={client}
            open={isEditOpen}
            onOpenChange={setIsEditOpen}
          />
          <DeleteClientDialog
            client={client}
            open={isDeleteOpen}
            onOpenChange={(open) => {
              setIsDeleteOpen(open);
              if (!open) {
                // Navigate back to clients list after deletion
                navigate('/admin/clients');
              }
            }}
          />
        </>
      )}
    </div>
  );
};

export default ClientDetails;
